import ManagedErrorToasts from '@/components/error/ManagedErrorToasts';
import type { SubmitTokenWithAgreementData } from '@/components/escript/token/types';
import { useErrorManagement } from '@/context/ErrorManagement';
import type { AlertColor } from '@mui/material';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

export type PreOnboardingStates =
  | 'SUBMIT_TOKEN'
  | 'SCRIPT_VALID'
  | 'SCRIPT_AWAITING_REVIEW'
  | 'SCRIPT_VALID_OUT_OF_STOCK'
  | 'DUPLICATE_TOKEN_PENDING_INVITE'
  | 'ACCOUNT_ALREADY_EXISTS'
  | 'INVALID_TOKEN_PENDING_INVITE'
  | 'ERX_FALLBACK_STRATEGY';

export interface PreOnboardingCustomer {
  email?: string;
  phone?: string;
  preOnboardUserId: number;
}

export interface AlertState {
  show: boolean;
  severity?: AlertColor;
  title?: string;
  content: string;
}

export interface PreOnboardingContextType {
  preOnboardingState: PreOnboardingStates;
  setPreOnboardingState: React.Dispatch<React.SetStateAction<PreOnboardingStates>>;
  customerDetails: null | PreOnboardingCustomer;
  setCustomerDetails: React.Dispatch<React.SetStateAction<null | PreOnboardingCustomer>>;
  alert: AlertState;
  setAlert: React.Dispatch<React.SetStateAction<AlertState>>;
  hideAlert: () => void;
  tokenInputRef: React.RefObject<HTMLDivElement | null>;
  resetOnboardingState: () => void;
}

export const PreOnboardingContext = createContext<PreOnboardingContextType | undefined>(undefined);

export const PreOnboardingStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preOnboardingState, setPreOnboardingState] = useState<PreOnboardingStates>('SUBMIT_TOKEN');
  const [customerDetails, setCustomerDetails] = useState<null | PreOnboardingCustomer>(null);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    severity: 'success',
    title: '',
    content: '',
  });
  const tokenInputRef = useRef<HTMLDivElement | null>(null);

  const hideAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };
  const location = useLocation();
  const { enqueueError } = useErrorManagement();
  const form = useForm<SubmitTokenWithAgreementData>({
    defaultValues: {
      tokenInput: '',
      agreementCheckbox: false,
    },
  });
  const { reset, setFocus } = form;

  // render any errors passed through from location on component mount
  useEffect(() => {
    if (location.state?.error) {
      enqueueError(location.state.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToMobileInput = useCallback(() => {
    const mobileInputContainer = tokenInputRef.current;

    if (mobileInputContainer) {
      mobileInputContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });

      setFocus('tokenInput');
    }
  }, [setFocus]);

  const resetOnboardingState = useCallback(() => {
    if (preOnboardingState !== 'SUBMIT_TOKEN') {
      setPreOnboardingState('SUBMIT_TOKEN');
      reset();
      scrollToMobileInput();
    }
  }, [preOnboardingState, reset, setPreOnboardingState, scrollToMobileInput]);

  const value = useMemo(() => {
    return {
      preOnboardingState,
      setPreOnboardingState,
      customerDetails,
      setCustomerDetails,
      alert,
      setAlert,
      hideAlert,
      tokenInputRef,
      resetOnboardingState,
    };
  }, [preOnboardingState, customerDetails, alert, resetOnboardingState]);

  return (
    <PreOnboardingContext.Provider value={value}>
      <ManagedErrorToasts />
      <FormProvider {...form}>{children}</FormProvider>
    </PreOnboardingContext.Provider>
  );
};
